import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Actions from "../../../components/Common/Actions";
import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../components/Common/TabBar";
import ListPageLayout from "../../../components/Layout";
import Deletepopup from "../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";
import "./menu.scss";

const Menu = () => {
  const navigate = useNavigate();

  // Sorting
  const [sort, setSort] = useState("newest");
  const [sortName, setSortName] = useState("Sort by");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [loader, setLoader] = useState(true);
  // Pagination and Data
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}health-article-menu/list`,
      initialParams: {
        sort,
        searchText: search,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "health-article-menu",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    setLoader(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search]);

  const handleActive = (id, status) => {
    setLoader(true);
    axios
      .patch(
        `${process.env.REACT_APP_API_URL}health-article-menu/update/${id}`,
        {
          active: !status,
        },
      )
      .then(() => {
        fetchData();
      });
  };

  const addNewMenu = {
    id: "addnewmenu",
    onClick: () => navigate("/addNewMenuForm"),
    style: { width: "240px" },
    label: "Add New Menu",
  };

  const addNewCategory = {
    id: "addnewcategory",
    onClick: () => navigate("/addNewCategoryForm"),
    style: { width: "240px", marginLeft: "10px" },
    label: "Add New Category",
  };

  const breadcrumbsList = useMemo(
    () => [{ text: "Manage Menu and Category", link: "" }],
    [],
  );

  const healthArticleNavData = [
    {
      name: "Health Articles",
      navigatePath: "/healtharticle",
    },
    {
      name: "Manage Menu & Category",
      navigatePath: "/healtharticlemenu" || "/Category",
    },
    {
      name: "HealthSy Stories",
      navigatePath: "/healthsystories",
    },
  ];

  const CategoryNavData = [
    {
      name: "Menu",
      navigatePath: "/healtharticlemenu",
    },
    {
      name: "Category",
      navigatePath: "/Category",
    },
  ];

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Menu",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Total Category",
      dataIndex: "total_category",
      key: "total_category",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active) => (active ? "Active" : "Inactive"),
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      width: 300,
      render: (_, record) => (
        <Actions
          recordActiveStatus={record.active}
          isView={true}
          isDelete={true}
          isEdit={true}
          isActive={true}
          onClickView={() => navigate(`/viewMenuForm/${record._id}`)}
          onClickEdit={() => navigate(`/addNewMenuEditForm/${record._id}`)}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickActive={() => handleActive(record._id, record.active)}
        />
      ),
    },
  ];

  const dataSource = records
    .filter((x) => x.show === undefined || x.show === true)
    .map((el) => ({ ...el, key: el._id }));

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={
        <div className="d-flex">
          <FilledButton props={addNewMenu} />
          <FilledButton props={addNewCategory} />
        </div>
      }
      titles={
        <div className="d-flex flex-column gap-2">
          <NavBarTitles titles={healthArticleNavData} />
          <NavBarTitles titles={CategoryNavData} />
        </div>
      }
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource,
        className: "mx-3 pt-2 table-scrollable blogs_menucategory_table",
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
    </ListPageLayout>
  );
};

export default Menu;
